import React, { useState, useEffect, useCallback } from 'react';

// Materials UI
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import DialogContent from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';

// Hooks
import { SignOut } from '../../../hooks/login'
import useNotify from '../../../hooks/notify'
import useUser from '../../../hooks/user/useUser';
import useSearchBranches from '../../../hooks/branch/useSearchBranches';
import useCashRegisters from "../../../hooks/cashRegisters/useCashRegisters";
import useBusinesses from '../../../hooks/business/useBusinesses';

// Components
import Modal from '../../modal';
import Dropdown from '../../../components/inputs/dropdown';
import Autocomplete from '../../../components/inputs/Autocomplete';

// Models
import { subChannels } from '../../../models/Channels';
import { hasBusinessTerminal } from '../../../utils/payment-utils';

const prepareBranchPvs = (businessSelected, branchSelected, pvSelected, cashRegisterSelected) => {
    if (!hasBusinessTerminal(businessSelected.country, businessSelected.isFranchise) || !cashRegisterSelected || cashRegisterSelected?.length === 0)
        return [branchSelected, pvSelected]

    const associatePvs = cashRegisterSelected.associatePvs;
    const pvSel = {
        ...pvSelected,
        hasTerminal: associatePvs.some(apv => apv.pvId === pvSelected.id && apv.hasTerminal)
    }

    const branchSel = {
        ...branchSelected,
        pvs: branchSelected.pvs.map(pv => {
            const associatedPv = associatePvs.find(apv => apv.pvId === pv.id);

            return {
                ...pv,
                hasTerminal: associatedPv?.hasTerminal ?? false
            };
        })
    };

    return [branchSel, pvSel]
}

const BranchModal = ({ change = false, onClose = () => { } }) => {
    //Hooks
    const { Notify, variants } = useNotify();
    const { businesses: userBusinesses, fullName, SelectBranch, business, branch, cashRegister, pv } = useUser();
    const { cashRegisters, setFilters: setCashRegisterFilters } = useCashRegisters({ isImprest: false });
    const { branches, loading, filters: branchFilters, setFilters: setBranchFilters } = useSearchBranches();
    const { businesses } = useBusinesses();

    //States
    const [pvSelected, setPvSelected] = useState(!pv?.id ? null : pv);
    const [pvsAvailable, setPvsAvailable] = useState([]);
    const [branchSelected, setBranchSelected] = useState(!branch?.id ? null : branch);
    const [businessSelected, setBusinessSelected] = useState(!business?.id ? null : business);
    const [cashRegisterSelected, setCashRegisterSelected] = useState(!cashRegister?.id ? null : cashRegister);
    const [cashRegsiterAvailable, setCashRegisterAvailabe] = useState([]);

    //Effects
    useEffect(() => {
        if (cashRegisters && cashRegisters.length > 0) {
            const cashRegsiterAvailable = cashRegisters
                .filter(cr => cr.active)
                .filter(cr => !cr.isImprest);

            const currentCashRegisterIsValid = cashRegisterSelected && cashRegsiterAvailable.some(cr => cr.id === cashRegisterSelected.id);
            
            const cashReg = currentCashRegisterIsValid 
                ? cashRegisterSelected 
                : (cashRegsiterAvailable.length > 0 ? cashRegsiterAvailable[0] : null);

            setCashRegisterSelected(cashReg)
            setCashRegisterAvailabe(cashRegsiterAvailable)
        } else {
            setCashRegisterSelected(null)
            setCashRegisterAvailabe([])
        }
    }, [cashRegisters, cashRegisterSelected])

    useEffect(() => {
        if (!!branchSelected && !!cashRegisterSelected?.associatePvs) {
            const pvs = branchSelected.pvs
                .filter(pv => pv.active)
                .filter(pv => pv.subChannel === subChannels.values.POS)
                .filter(pv => cashRegisterSelected.associatePvs.some(pva => pva.pvId === pv.id));

            const currentPvIsValid = pvSelected && pvs.some(pv => pv.id === pvSelected.id);
            
            const pv = currentPvIsValid 
                ? pvSelected 
                : (pvs.length > 0 ? pvs[0] : null);

            if (!pv)
                Notify("La sucursal seleccionada no cuenta con ningún pv disponible.", variants.ERROR);

            setPvSelected(pv);
            setPvsAvailable(pvs);
        }
    }, [branchSelected, cashRegisterSelected?.associatePvs, Notify, variants, pvSelected])

    const GetBusiness = useCallback(() => {
        if (!businesses)
            return [];

        if (!userBusinesses)
            return businesses;

        return businesses.filter(b => userBusinesses.some(ub => ub.id === b.id)) || []
    }, [businesses, userBusinesses])

    useEffect(() => {
        const businesses = GetBusiness();

        if (businesses.length > 0 && business?.id === 0) {
            const business1 = businesses[0];
            setBusinessSelected(business1);
            setBranchFilters(state => ({ ...state, businessId: business1.id }));
        } else if (business) {
            setBusinessSelected(business);
            setBranchFilters(state => ({ ...state, businessId: business.id }));
        }
    }, [GetBusiness, setBranchFilters, business])

    //Handlers
    const handleSave = () => {
        if (!branchSelected) {
            Notify("Debes seleccionar una sucursal para continuar.", variants.WARNING);
            return;
        }

        if (!pvSelected || pvSelected.id === 0) {
            Notify("La sucursal seleccionada no cuenta con ningun pv disponible.", variants.ERROR);
            return;
        }

        if (!cashRegisterSelected) {
            Notify("Debes seleccionar una caja para continuar.", variants.WARNING);
            return;
        }

        const [branchSel, pvSel] = prepareBranchPvs(businessSelected, branchSelected, pvSelected, cashRegisterSelected);
        SelectBranch(businessSelected, branchSel, pvSel, cashRegisterSelected);
        onClose();
    }

    const handleBranchChange = (value) => {
        const branch = branches.find(b => b.id === value?.id);
        setBranchSelected(branch);
        setCashRegisterFilters(state => ({ ...state, branchId: branch?.id }))
    }

    let timerId;
    const handleChangeInputBranchAutocomplete = (event, value) => {
        clearTimeout(timerId)

        timerId = setTimeout(() => setBranchFilters({
            ...branchFilters,
            textFilter: value
        }), 500);
    }

    const handlePvChange = (event) => {
        const pv = pvsAvailable.find(pv => pv.id === event.target.value);
        setPvSelected(pv);
    }

    const handleCashRegisterChange = (event) => {
        const cr = cashRegisters.find(cr => cr.id === event.target.value);
        setCashRegisterSelected(cr);
    }

    const handleBusinessChange = (event) => {
        const b = businesses.find(cr => cr.id === event.target.value);
        setBranchSelected(null);
        setCashRegisterSelected(null);
        setPvSelected(null);
        setBranchFilters({ ...branchFilters, businessId: b.id });
        setBusinessSelected(b);
    }

    return (
        <Modal
            open
            disableEnforceFocus
            disableAutoFocus
            disableEscapeKeyDown
            customButtons={() =>
                <DialogContent>
                    <Button
                        onClick={SignOut}
                        color="primary"
                    >
                        Cerrar sesion
                    </Button>
                    <Button
                        name="branchModalSave"
                        variant='outlined'
                        onClick={handleSave}
                        color="primary"
                    >
                        Guardar cambios
                    </Button>
                </DialogContent>
            }
        >
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Typography variant='h4' align='center'>
                        {!change ? 'Bienvenido' : 'Hola'}
                    </Typography>
                    <Typography variant='h3' align='center'>
                        {fullName}
                    </Typography>
                </Grid>
                <Grid size={12}>
                    {
                        !change &&
                        <DialogContentText>
                            Selecciona la sucursal en la cual vas a operar y se te asignará un punto de venta que
                            se encuentre disponible, que tengas un excelente día.
                        </DialogContentText>
                    }
                    {
                        change &&
                        <DialogContentText>
                            Selecciona la caja y se te asignará un punto de venta que
                            se encuentre disponible, que tengas un excelente día.
                        </DialogContentText>
                    }
                </Grid>
                <Grid size={12}>
                    <Dropdown
                        fullWidth
                        disabled={change || GetBusiness()?.length <= 1}
                        onChange={handleBusinessChange}
                        size='small'
                        label="Empresa"
                        name='business'
                        getOptionLabel={(option) => option?.name}
                        getOptionValue={(option) => option?.id}
                        value={businessSelected?.id || ''}
                        values={GetBusiness()}
                    />
                </Grid>
                <Grid size={12}>
                    <Autocomplete
                        name='branch'
                        fullWidth
                        disableClearable
                        size='small'
                        loadingText='Buscando sucursales...'
                        TextProps={{ label: 'Sucursal' }}
                        loading={loading}
                        options={branches}
                        value={branchSelected}
                        getOptionLabel={(option) => option?.name}
                        onChange={(e, value) => handleBranchChange(value)}
                        onInputChange={handleChangeInputBranchAutocomplete}
                        disabled={!businessSelected || change}
                    />
                </Grid>
                <Grid size={6}>
                    <Dropdown
                        fullWidth
                        disabled={cashRegsiterAvailable.length <= 1}
                        onChange={handleCashRegisterChange}
                        size='small'
                        label="Caja"
                        name='cashRegister'
                        getOptionLabel={(option) => option?.name}
                        getOptionValue={(option) => option?.id}
                        value={cashRegisterSelected?.id || ''}
                        values={cashRegsiterAvailable}
                    />
                </Grid>
                <Grid size={6}>
                    <Dropdown
                        fullWidth
                        disabled={pvsAvailable.length <= 1}
                        onChange={handlePvChange}
                        size='small'
                        label="Punto de venta"
                        name='pv'
                        getOptionLabel={(option) => option?.name}
                        getOptionValue={(option) => option?.id}
                        value={pvSelected?.id || ''}
                        values={pvsAvailable}
                    />
                </Grid>
            </Grid>
        </Modal>
    );
}

export default BranchModal;